import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PremiumBanner } from "@/components/PremiumBanner";
import { api, euro, formatApiError } from "@/lib/api";
import { downloadShoppingListPdf } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  Search, Plus, Minus, Trash2, MapPin, TrendingDown, Store, Bell,
  Save, AlertTriangle, CheckCircle2, Split, ExternalLink, Loader2, Tag, Heart,
  LocateFixed, ScanLine, BarChart3, Upload, ChefHat, Share2, Copy, Printer, Clock, ShoppingBasket,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const IDF_ZONES = [
  { dept: "Paris (75)", options: [["75001", "Paris 1er"], ["75004", "Paris 4e"], ["75008", "Paris 8e"], ["75011", "Paris 11e"], ["75012", "Paris 12e"], ["75013", "Paris 13e"], ["75015", "Paris 15e"], ["75017", "Paris 17e"], ["75019", "Paris 19e"], ["75020", "Paris 20e"]] },
  { dept: "Hauts-de-Seine (92)", options: [["92000", "Nanterre"], ["92100", "Boulogne-Billancourt"], ["92130", "Issy-les-Moulineaux"]] },
  { dept: "Seine-Saint-Denis (93)", options: [["93000", "Bobigny"], ["93100", "Montreuil"], ["93200", "Saint-Denis"], ["93600", "Aulnay-sous-Bois"]] },
  { dept: "Val-de-Marne (94)", options: [["94000", "Créteil"], ["94200", "Ivry-sur-Seine"], ["94220", "Charenton-le-Pont"], ["94400", "Vitry-sur-Seine"]] },
  { dept: "Yvelines (78)", options: [["78000", "Versailles"], ["78140", "Vélizy-Villacoublay"], ["78300", "Poissy"]] },
  { dept: "Essonne (91)", options: [["91000", "Évry-Courcouronnes"], ["91100", "Corbeil-Essonnes"], ["91300", "Massy"]] },
  { dept: "Val-d'Oise (95)", options: [["95000", "Cergy"], ["95100", "Argenteuil"], ["95200", "Sarcelles"]] },
  { dept: "Seine-et-Marne (77)", options: [["77000", "Melun"], ["77100", "Meaux"], ["77500", "Chelles"]] },
];

export default function Comparateur() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [basket, setBasket] = useState(() => {
    const saved = localStorage.getItem("pmc_basket");
    return saved ? JSON.parse(saved) : [];
  });
  const [postcode, setPostcode] = useState("75012");
  const [radius, setRadius] = useState(15);
  const [substitution, setSubstitution] = useState(false);
  const [twoStore, setTwoStore] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [geoStatus, setGeoStatus] = useState("");
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [histProduct, setHistProduct] = useState(null);
  const [histData, setHistData] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histLimited, setHistLimited] = useState(false);
  const [savings, setSavings] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [menuDays, setMenuDays] = useState("4");
  const [menuPeople, setMenuPeople] = useState("2");
  const [menuPrefs, setMenuPrefs] = useState("");
  const [addr, setAddr] = useState("");
  const [addrResults, setAddrResults] = useState([]);
  const addrRef = useRef();
  const fileRef = useRef();
  const debounceRef = useRef();

  useEffect(() => {
    localStorage.setItem("pmc_basket", JSON.stringify(basket));
  }, [basket]);

  useEffect(() => {
    if (user) api.get("/savings/summary").then((r) => setSavings(r.data)).catch(() => {});
  }, [user]);

  const doSearch = useCallback((q) => {
    setSearching(true);
    api.get(`/products/search`, { params: { q, limit: 20 } })
      .then((r) => setResults(r.data))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  const addToBasket = (p) => {
    setBasket((b) => {
      const found = b.find((x) => x.product_id === p.id);
      if (found) return b.map((x) => (x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x));
      return [...b, { product_id: p.id, quantity: 1, name: p.name, brand: p.brand, unit: p.unit, min_price: p.min_price }];
    });
    toast.success(`${p.name} ajouté au panier`);
  };

  const setQty = (id, delta) =>
    setBasket((b) => b.map((x) => (x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x)));

  const removeItem = (id) => setBasket((b) => b.filter((x) => x.product_id !== id));

  const compare = async () => {
    if (basket.length === 0) { toast.error("Ajoutez des produits d'abord"); return; }
    setComparing(true);
    setComparison(null);
    try {
      const { data } = await api.post("/compare", {
        items: basket.map((b) => ({ product_id: b.product_id, quantity: b.quantity })),
        postcode, radius_km: radius, allow_substitution: substitution, two_store_threshold: 3,
        lat: coords?.lat, lng: coords?.lng,
      });
      setComparison(data);
      if (user && data.best_single) {
        api.post("/history", {
          best_store: data.best_single.store.name,
          best_total: data.best_single.total,
          item_count: basket.length,
          savings: data.results.length > 1 ? Math.round((data.results[data.results.length - 1].total - data.best_single.total) * 100) / 100 : 0,
        }).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setComparing(false);
    }
  };

  const addAlert = async (productId) => {
    if (!user) { toast.error("Connectez-vous pour créer une alerte"); return; }
    try {
      await api.post(`/alerts/${productId}`);
      toast.success("Alerte de baisse de prix créée");
    } catch (e) { toast.error("Erreur"); }
  };

  const saveBasket = async () => {    if (!user) { toast.error("Connectez-vous pour sauvegarder"); return; }
    try {
      await api.post("/baskets", {
        name: saveName || `Panier ${new Date().toLocaleDateString("fr-FR")}`,
        items: basket.map((b) => ({ product_id: b.product_id, quantity: b.quantity })),
      });
      toast.success("Panier sauvegardé");
      setSaveOpen(false);
      setSaveName("");
    } catch (e) { toast.error("Erreur lors de la sauvegarde"); }
  };

  const requestLocation = (silent = false) => {
    if (!navigator.geolocation) { if (!silent) setGeoStatus("Géolocalisation non disponible"); return; }
    if (!silent) setGeoStatus("Localisation...");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus("Position détectée");
        toast.success("Position détectée — magasins triés par distance réelle");
      },
      () => {
        if (!silent) { setGeoStatus("Autorisation refusée"); toast.error("Impossible d'obtenir votre position"); }
        localStorage.setItem("pmc_geo_declined", "1");
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // Auto-detect location on first open (or reuse coords passed from the landing page)
  useEffect(() => {
    const stored = localStorage.getItem("pmc_coords");
    if (stored) {
      try { setCoords(JSON.parse(stored)); } catch (e) {}
      localStorage.removeItem("pmc_coords");
      setGeoStatus("Position détectée");
      return;
    }
    if (!localStorage.getItem("pmc_geo_declined") && navigator.geolocation) {
      requestLocation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openHistory = async (productId, name) => {
    setHistProduct({ id: productId, name });
    setHistLoading(true);
    setHistData([]);
    try {
      const { data } = await api.get(`/products/${productId}/history`);
      setHistLimited(data.limited);
      setHistData(data.points.map((d) => ({ ...d, label: new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) })));
    } catch (e) { toast.error("Historique indisponible"); }
    finally { setHistLoading(false); }
  };

  const compressToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxW = 1100;
          const scale = Math.min(1, maxW / img.width);
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { toast.error("Connectez-vous pour scanner un ticket"); return; }
    setOcrLoading(true);
    try {
      const b64 = await compressToBase64(file);
      const { data } = await api.post("/ocr/receipt", { image_base64: b64 });
      if (data.matched.length === 0) {
        toast.error("Aucun produit reconnu sur ce ticket");
      } else {
        setBasket((b) => {
          let next = [...b];
          data.matched.forEach((m) => {
            const found = next.find((x) => x.product_id === m.product_id);
            if (found) next = next.map((x) => (x.product_id === m.product_id ? { ...x, quantity: x.quantity + m.quantity } : x));
            else next.push({ product_id: m.product_id, quantity: m.quantity, name: m.name, brand: m.brand, unit: m.unit });
          });
          return next;
        });
        toast.success(`${data.matched.length} produit(s) ajouté(s) depuis le ticket`);
        if (data.unmatched.length) toast.message(`Non reconnus : ${data.unmatched.slice(0, 5).join(", ")}`);
      }
      setOcrOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Analyse impossible");
    } finally {
      setOcrLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generateMenu = async () => {
    setMenuLoading(true);
    setMenuData(null);
    try {
      const { data } = await api.post("/menu/generate", {
        days: parseInt(menuDays) || 4, people: parseInt(menuPeople) || 2, preferences: menuPrefs,
      });
      setMenuData(data);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("Limite gratuite atteinte", {
          description: "Passez à Premium pour des menus illimités",
          action: { label: "Premium", onClick: () => nav("/premium") },
        });
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Génération impossible");
      }
    } finally { setMenuLoading(false); }
  };

  const addMenuToBasket = () => {
    if (!menuData?.basket?.length) return;
    setBasket((b) => {
      let next = [...b];
      menuData.basket.forEach((m) => {
        const found = next.find((x) => x.product_id === m.product_id);
        if (found) next = next.map((x) => (x.product_id === m.product_id ? { ...x, quantity: x.quantity + m.quantity } : x));
        else next.push({ product_id: m.product_id, quantity: m.quantity, name: m.name, brand: m.brand, unit: m.unit });
      });
      return next;
    });
    toast.success(`${menuData.basket.length} ingrédient(s) ajouté(s) au panier`);
    setMenuOpen(false);
  };

  const searchAddress = (v) => {
    setAddr(v);
    clearTimeout(addrRef.current);
    if (v.trim().length < 3) { setAddrResults([]); return; }
    addrRef.current = setTimeout(() => {
      api.get("/geocode", { params: { q: v } }).then((r) => setAddrResults(r.data)).catch(() => setAddrResults([]));
    }, 350);
  };

  const selectAddress = (a) => {
    setCoords({ lat: a.lat, lng: a.lng });
    setGeoStatus(a.label);
    setAddr(a.label);
    setAddrResults([]);
    if (a.postcode) setPostcode(a.postcode);
    toast.success("Adresse sélectionnée");
  };

  const shareBasket = async () => {
    if (basket.length === 0) { toast.error("Panier vide"); return; }
    try {
      const { data } = await api.post("/share", {
        name: `Panier de ${user?.name || "PanierMoinsCher"}`,
        items: basket.map((b) => ({ product_id: b.product_id, quantity: b.quantity })),
      });
      const url = `${window.location.origin}/panier/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success("Lien copié !", { description: url });
    } catch (e) { toast.error("Partage impossible"); }
  };

  const printList = async () => {
    if (!best) return;
    const two = comparison?.two_store;
    const groups = [];
    if (twoStore && two && two.worth_it) {
      groups.push({ title: two.store_a.name, lines: two.split_a });
      groups.push({ title: two.store_b.name, lines: two.split_b });
    } else {
      groups.push({
        title: best.store.name,
        lines: best.lines.filter((l) => l.available).map((l) => ({ name: l.name, quantity: l.quantity, line_total: l.line_total })),
      });
    }
    try {
      await downloadShoppingListPdf(groups);
      toast.success("Liste PDF téléchargée");
    } catch (e) {
      toast.error("Impossible de générer le PDF");
    }
  };

  const best = comparison?.best_single;
  const ranked = comparison?.results || [];
  const two = comparison?.two_store;
  const worst = ranked.length ? ranked[ranked.length - 1] : null;
  const maxSaving = best && worst ? Math.max(0, worst.total - best.total) : 0;

  return (
    <div className="App min-h-screen bg-background">
      <Header />

      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleReceipt} data-testid="receipt-file-input" />

      <Dialog open={ocrOpen} onOpenChange={setOcrOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Scanner un ticket de caisse</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Prenez ou importez une photo de votre ticket. On détecte les produits et on remplit votre panier automatiquement.
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={ocrLoading}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/40 p-8 text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
              data-testid="ocr-upload-zone"
            >
              {ocrLoading ? <Loader2 className="h-7 w-7 animate-spin" /> : <Upload className="h-7 w-7" />}
              <span className="text-sm font-medium">{ocrLoading ? "Analyse du ticket en cours..." : "Choisir une photo de ticket"}</span>
            </button>
            {!user && <p className="text-xs text-destructive">Connectez-vous pour utiliser le scan de ticket.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histProduct} onOpenChange={(o) => !o && setHistProduct(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Historique du prix</DialogTitle></DialogHeader>
          {histProduct && <p className="-mt-2 text-sm text-muted-foreground">{histProduct.name}</p>}
          <div className="mt-2 h-64 w-full">
            {histLoading ? (
              <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={histData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" domain={["auto", "auto"]} tickFormatter={(v) => `${v}€`} />
                  <Tooltip formatter={(v) => euro(v)} labelStyle={{ color: "#0f172a" }} />
                  <Line type="monotone" dataKey="price" stroke="hsl(160 84% 39%)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {histData.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Prix le plus bas relevé : <b className="text-primary">{euro(Math.min(...histData.map((d) => d.price)))}</b> · actuel : <b className="text-secondary">{euro(histData[histData.length - 1].price)}</b>
            </p>
          )}
          {histLimited && (
            <button onClick={() => nav("/premium")} className="mt-1 flex w-full items-center justify-center gap-1 rounded-lg bg-accent/10 px-3 py-2 text-xs font-medium text-accent transition-colors hover:bg-accent/20" data-testid="history-upsell">
              <ChefHat className="h-3 w-3" /> Historique limité à 4 semaines — passez à Premium pour l'historique complet
            </button>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ChefHat className="h-4 w-4 text-accent" /> Idées de repas de la semaine</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Repas</Label>
              <Input type="number" min="1" max="7" value={menuDays} onChange={(e) => setMenuDays(e.target.value)} className="mt-1.5" data-testid="menu-days-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Personnes</Label>
              <Input type="number" min="1" value={menuPeople} onChange={(e) => setMenuPeople(e.target.value)} className="mt-1.5" data-testid="menu-people-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Régime</Label>
              <Input value={menuPrefs} onChange={(e) => setMenuPrefs(e.target.value)} placeholder="ex: végétarien" className="mt-1.5" data-testid="menu-prefs-input" />
            </div>
          </div>
          <Button onClick={generateMenu} disabled={menuLoading} className="rounded-full" data-testid="menu-generate-confirm">
            {menuLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ChefHat className="mr-2 h-4 w-4" />} Générer mes repas
          </Button>

          {menuData && (
            <div className="space-y-3" data-testid="menu-results">
              <div className="grid grid-cols-1 gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Budget estimé</p>
                  <p className="font-display text-2xl font-800 text-primary" data-testid="menu-budget">{euro(menuData.estimated_budget)}</p>
                  <p className="text-xs text-muted-foreground">au meilleur prix relevé · {menuData.basket.length} produits</p>
                </div>
                {menuData.nutrition && (
                  <div data-testid="menu-nutrition">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Équilibre nutritionnel</p>
                    {menuData.nutrition.kcal_per_person_per_day ? (
                      <p className="text-sm font-semibold text-secondary">~{menuData.nutrition.kcal_per_person_per_day} kcal / pers / jour</p>
                    ) : null}
                    <p className="mt-1 text-xs text-muted-foreground">{menuData.nutrition.balance}</p>
                    {menuData.nutrition.tips && <p className="mt-1 flex items-start gap-1 text-xs text-accent"><CheckCircle2 className="mt-0.5 h-3 w-3 flex-none" /> {menuData.nutrition.tips}</p>}
                  </div>
                )}
              </div>
              {menuData.meals.map((m, i) => (
                <div key={i} className="rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-display font-700 text-secondary">{m.title}</p>
                    {m.day && <Badge variant="secondary">{m.day}</Badge>}
                  </div>
                  {m.description && <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.products.map((p, j) => (
                      <Badge key={j} variant="outline" className="border-primary/30 text-secondary">{p.quantity}× {p.name}</Badge>
                    ))}
                  </div>
                </div>
              ))}
              <Button onClick={addMenuToBasket} className="w-full rounded-full" data-testid="menu-add-basket-btn">
                <Plus className="mr-2 h-4 w-4" /> Ajouter les {menuData.basket.length} ingrédients au panier
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-800 tracking-tight text-secondary">Comparateur de panier</h1>
        <p className="mt-1 text-muted-foreground">Composez votre liste et trouvez le magasin le moins cher près de chez vous.</p>

        {user && savings && savings.comparisons > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm" data-testid="home-savings-badge">
            <TrendingDown className="h-4 w-4 text-primary" />
            <span className="text-secondary">Vous avez économisé <b className="text-primary">{euro(savings.this_month)}</b> ce mois-ci</span>
            <span className="text-muted-foreground">· {euro(savings.all_time)} au total</span>
          </div>
        )}

        <div className="mt-6"><PremiumBanner context="comparateur" /></div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* SIDEBAR */}
          <div className="space-y-6 lg:col-span-1">
            {/* Search */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-700 text-secondary">
                <Search className="h-4 w-4 text-primary" /> Rechercher des produits
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Lait, Nutella, pâtes..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9"
                  data-testid="product-search-input"
                />
              </div>
              <Button
                variant="outline"
                className="mt-3 w-full rounded-full border-dashed"
                onClick={() => setOcrOpen(true)}
                data-testid="scan-receipt-btn"
              >
                <ScanLine className="mr-2 h-4 w-4 text-primary" /> Scanner un ticket de caisse
              </Button>
              <Button
                variant="outline"
                className="mt-2 w-full rounded-full border-dashed"
                onClick={() => setMenuOpen(true)}
                data-testid="menu-generate-btn"
              >
                <ChefHat className="mr-2 h-4 w-4 text-accent" /> Idées de repas de la semaine
              </Button>
              <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="search-results">
                {searching && <p className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></p>}
                {!searching && results.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-border p-3 transition-colors hover:bg-muted/60" data-testid={`search-item-${p.id}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} loading="lazy" className="h-11 w-11 flex-none rounded-lg border border-border object-cover" />
                      ) : (
                        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground"><ShoppingBasket className="h-5 w-5" /></div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-secondary">{p.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{p.brand}{p.unit ? ` · ${p.unit}` : ""} · dès {euro(p.min_price)}</p>
                      </div>
                    </div>
                    <div className="flex flex-none items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-secondary" onClick={() => openHistory(p.id, p.name)} data-testid={`history-btn-${p.id}`}>
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => addToBasket(p)} data-testid={`add-btn-${p.id}`}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {!searching && results.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Aucun produit</p>}
              </div>
            </div>

            {/* Location + options */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-700 text-secondary">
                <MapPin className="h-4 w-4 text-primary" /> Zone de recherche · Île-de-France
              </h2>
              <div className="space-y-4">
                <div className="relative">
                  <Label className="text-xs text-muted-foreground">Adresse précise</Label>
                  <Input
                    value={addr}
                    onChange={(e) => searchAddress(e.target.value)}
                    placeholder="12 rue de la Paix, Paris"
                    className="mt-1.5"
                    data-testid="address-input"
                  />
                  {addrResults.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg" data-testid="address-suggestions">
                      {addrResults.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => selectAddress(a)}
                          className="block w-full px-3 py-2 text-left text-sm text-secondary transition-colors hover:bg-muted"
                          data-testid={`address-option-${i}`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Ou choisissez votre commune (Île-de-France)</Label>
                  <Select value={postcode} onValueChange={setPostcode}>
                    <SelectTrigger className="mt-1.5" data-testid="postcode-select"><SelectValue placeholder="Sélectionnez une commune" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {IDF_ZONES.map((z) => (
                        <SelectGroup key={z.dept}>
                          <SelectLabel>{z.dept}</SelectLabel>
                          {z.options.map(([pc, name]) => (
                            <SelectItem key={pc} value={pc} data-testid={`postcode-option-${pc}`}>{pc} · {name}</SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="w-full rounded-full" onClick={() => requestLocation(false)} data-testid="geolocate-btn">
                  <LocateFixed className="mr-2 h-4 w-4 text-primary" /> Utiliser ma position
                </Button>
                {coords && (
                  <p className="-mt-2 flex items-center gap-1 text-xs text-primary" data-testid="geo-status">
                    <CheckCircle2 className="h-3 w-3" /> {geoStatus} ({coords.lat.toFixed(3)}, {coords.lng.toFixed(3)})
                  </p>
                )}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Rayon</Label>
                    <span className="text-sm font-semibold text-secondary">{radius} km</span>
                  </div>
                  <Slider value={[radius]} min={2} max={50} step={1} onValueChange={(v) => setRadius(v[0])} className="mt-3" data-testid="radius-slider" />
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/60 p-3">
                  <div><p className="text-sm font-medium text-secondary">Substituts moins chers</p><p className="text-xs text-muted-foreground">Produits équivalents</p></div>
                  <Switch checked={substitution} onCheckedChange={setSubstitution} data-testid="substitution-toggle" />
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/60 p-3">
                  <div><p className="text-sm font-medium text-secondary">Optimisation 2 magasins</p><p className="text-xs text-muted-foreground">Répartir la liste</p></div>
                  <Switch checked={twoStore} onCheckedChange={setTwoStore} data-testid="twostore-toggle" />
                </div>
              </div>
            </div>

            {/* Basket */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-lg font-700 text-secondary">
                  <Store className="h-4 w-4 text-primary" /> Mon panier
                </h2>
                <Badge variant="secondary" data-testid="basket-count">{basket.length}</Badge>
              </div>
              <div className="space-y-2" data-testid="basket-list">
                <AnimatePresence>
                  {basket.map((it) => (
                    <motion.div key={it.product_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
                      className="flex items-center gap-2 rounded-lg border border-border p-2.5" data-testid={`basket-item-${it.product_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-secondary">{it.name}</p>
                        <p className="text-xs text-muted-foreground">{it.unit}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, -1)} data-testid={`qty-minus-${it.product_id}`}><Minus className="h-3 w-3" /></Button>
                        <span className="w-6 text-center text-sm font-semibold" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, 1)} data-testid={`qty-plus-${it.product_id}`}><Plus className="h-3 w-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(it.product_id)} data-testid={`remove-${it.product_id}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {basket.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Votre panier est vide</p>}
              </div>

              <div className="mt-4 flex gap-2">
                <Button className="flex-1 rounded-full" onClick={compare} disabled={comparing || basket.length === 0} data-testid="compare-btn">
                  {comparing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TrendingDown className="mr-2 h-4 w-4" />} Comparer
                </Button>
                <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full" disabled={basket.length === 0} data-testid="save-basket-btn"><Save className="h-4 w-4" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Sauvegarder ce panier</DialogTitle></DialogHeader>
                    <Input placeholder="Nom du panier (ex: Courses semaine)" value={saveName} onChange={(e) => setSaveName(e.target.value)} data-testid="save-basket-name" />
                    <DialogFooter>
                      <Button onClick={saveBasket} className="rounded-full" data-testid="save-basket-confirm">Sauvegarder</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" size="icon" className="rounded-full" disabled={basket.length === 0} onClick={shareBasket} data-testid="share-basket-btn">
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* MAIN RESULTS */}
          <div className="lg:col-span-2">
            {!comparison && (
              <div className="flex h-full min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card/50 p-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <TrendingDown className="h-8 w-8" />
                </div>
                <h3 className="font-display text-xl font-700 text-secondary">Prêt à économiser ?</h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">Ajoutez des produits à votre panier puis lancez la comparaison pour voir les prix par magasin.</p>
              </div>
            )}

            {comparison && (
              <div className="space-y-6">
                {/* Summary */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-secondary p-6 text-white sm:grid-cols-3" data-testid="summary-widget">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-200">Meilleur magasin</p>
                    <p className="mt-1 font-display text-lg font-800">{best?.store.name}</p>
                    <p className="text-xs text-emerald-100/80">{best?.store.distance_km} km · {best?.store.retailer?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-200">Total panier</p>
                    <p className="mt-1 font-display text-3xl font-800 text-primary">{euro(best?.total)}</p>
                    <p className="text-xs text-emerald-100/80">Couverture {best?.coverage}%</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-emerald-200">Économies max</p>
                    <p className="mt-1 font-display text-3xl font-800 text-accent">{euro(maxSaving)}</p>
                    <p className="text-xs text-emerald-100/80">vs magasin le plus cher</p>
                  </div>
                </motion.div>

                <div className="flex justify-end">
                  <Button variant="outline" className="rounded-full" onClick={printList} data-testid="print-list-btn">
                    <Printer className="mr-2 h-4 w-4" /> Télécharger (PDF)
                  </Button>
                </div>

                {/* Two-store optimization */}
                {twoStore && two && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl border p-6 ${two.worth_it ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`} data-testid="twostore-result">
                    <div className="flex items-start gap-3">
                      <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg ${two.worth_it ? "bg-accent text-white" : "bg-muted text-muted-foreground"}`}>
                        <Split className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-700 text-secondary">Répartir sur 2 magasins</h3>
                        {two.worth_it ? (
                          <p className="text-sm text-muted-foreground">
                            En répartissant sur <b>{two.store_a.name}</b> et <b>{two.store_b.name}</b>, vous payez{" "}
                            <b className="text-secondary">{euro(two.total)}</b> et économisez{" "}
                            <b className="text-accent">{euro(two.savings_vs_single)}</b> de plus (couverture {two.coverage}%,
                            +{two.extra_distance_km} km entre les deux).
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Un seul magasin reste le meilleur choix ici. Répartir sur deux magasins n'économiserait que{" "}
                            {euro(Math.max(0, two.savings_vs_single))} — pas assez pour justifier un second trajet.
                          </p>
                        )}
                        {two.worth_it && (
                          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {[["a", two.store_a, two.split_a], ["b", two.store_b, two.split_b]].map(([k, st, split]) => (
                              <div key={k} className="rounded-lg border border-border bg-card p-4">
                                <p className="text-sm font-semibold text-secondary">{st.name}</p>
                                <ul className="mt-2 space-y-1">
                                  {split.map((s, i) => (
                                    <li key={i} className="flex justify-between text-xs text-muted-foreground">
                                      <span className="truncate pr-2">{s.quantity}× {s.name}</span><span>{euro(s.line_total)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Store cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {ranked.map((r, i) => (
                    <motion.div
                      key={r.store.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`relative rounded-xl bg-card p-6 ${r.is_cheapest ? "tracing-beam" : "border border-border transition-transform duration-300 hover:-translate-y-1 hover:shadow-md"}`}
                      data-testid={`store-card-${r.store.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-3 w-3 rounded-full" style={{ background: r.store.retailer?.color }} />
                            <h3 className="font-display text-lg font-700 text-secondary">{r.store.name}</h3>
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground">{r.store.distance_km} km · {r.store.address}</p>
                          {r.freshest_date && (
                            <div className="mt-1.5 flex items-center gap-1.5" data-testid={`freshness-${r.store.id}`}>
                              <Badge variant="outline" className={`gap-1 text-[10px] ${r.price_source === "real" ? "border-primary/40 text-primary" : r.price_source === "admin" ? "border-secondary/30 text-secondary" : "border-muted-foreground/30 text-muted-foreground"}`}>
                                <Clock className="h-3 w-3" />
                                {r.price_source === "real" ? "Prix réels" : r.price_source === "admin" ? "Prix vérifié" : "Données démo"}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">MAJ {new Date(r.freshest_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</span>
                            </div>
                          )}
                        </div>
                        {r.is_cheapest && <Badge className="bg-primary text-white" data-testid={`cheapest-badge-${r.store.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Le moins cher</Badge>}
                      </div>

                      <div className="mt-4 flex items-end justify-between">
                        <div>
                          <p className="font-display text-3xl font-800 text-secondary" data-testid={`store-total-${r.store.id}`}>{euro(r.total)}</p>
                          <p className="text-xs text-muted-foreground">{r.covered}/{r.total_items} articles</p>
                        </div>
                        <a href={r.store.drive_url} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm" className="rounded-full" data-testid={`drive-btn-${r.store.id}`}>
                            Drive <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        </a>
                      </div>

                      <div className="mt-4">
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Taux de couverture</span>
                          <span className="font-semibold text-secondary">{r.coverage}%</span>
                        </div>
                        <Progress value={r.coverage} className="h-2" />
                      </div>

                      {r.missing.length > 0 && (
                        <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-accent/10 p-2.5 text-xs text-accent-foreground">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-accent" />
                          <span className="text-muted-foreground"><b className="text-secondary">Manquants:</b> {r.missing.join(", ")}</span>
                        </div>
                      )}

                      {r.lines.some((l) => l.promo || l.substituted) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {r.lines.filter((l) => l.promo).length > 0 && (
                            <Badge variant="outline" className="border-primary/30 text-primary"><Tag className="mr-1 h-3 w-3" />{r.lines.filter((l) => l.promo).length} promo</Badge>
                          )}
                          {r.lines.filter((l) => l.substituted).length > 0 && (
                            <Badge variant="outline" className="border-accent/30 text-accent">{r.lines.filter((l) => l.substituted).length} substitut</Badge>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Alerts on basket products */}
                {user && (
                  <div className="rounded-xl border border-border bg-card p-6">
                    <h3 className="mb-3 flex items-center gap-2 font-display text-base font-700 text-secondary"><Bell className="h-4 w-4 text-primary" /> Suivre les prix</h3>
                    <div className="flex flex-wrap gap-2">
                      {basket.map((it) => (
                        <Button key={it.product_id} variant="outline" size="sm" className="rounded-full" onClick={() => addAlert(it.product_id)} data-testid={`alert-btn-${it.product_id}`}>
                          <Heart className="mr-1 h-3 w-3" /> {it.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
